import { useCallback, useEffect, useRef, useState } from "react"

import { useScopedStorageKey } from "@/workspaces/scope"

import type { MetadataFilter } from "@/types/MetadataFilter"
import { TIMESTAMP_MODE } from "@/types/tables/ConversationsTable"

import type {
  ConversationsMetricValueOperator,
  ConversationsMetricValueType,
} from "../types"
import type { ColumnCallbacks, FilterState } from "../types/filterState"
import {
  AUTO_SAVE_DELAY,
  type SavedView,
  type SavedViewConfig,
} from "../types/savedViews"
import type { SortDescriptor } from "../types/sortDescriptor"
import { normalizeViewDisplayConfig } from "../utils/normalizeViewDisplayConfig"
import { savedViewsStorage } from "../utils/savedViewsStorage"
import { viewConfigBuilder } from "../utils/viewConfigBuilder"

interface UrlSyncedState {
  dateRange: { start: Date | null; end: Date | null }
  metricIds: string[]
  agentIds: string[]
  tagFilters: string[]
  projectIds: string[]
  metricValueFilters: Array<{
    metricId: string
    metricDisplayName: string
    operator: ConversationsMetricValueOperator
    value: string
    valueType: ConversationsMetricValueType
  }>
  metadataFilters: MetadataFilter[]
}

const URL_DEFAULTS: UrlSyncedState = {
  dateRange: { start: null, end: null },
  metricIds: [],
  agentIds: [],
  tagFilters: [],
  projectIds: [],
  metricValueFilters: [],
  metadataFilters: [],
}

const DEFAULT_SORT_DESCRIPTOR: SortDescriptor = {
  column: TIMESTAMP_MODE.CREATED_AT,
  direction: "descending",
}

interface UseConversationsViewStateProps {
  isReady: boolean
  isSharedLink: boolean
  filterState: FilterState
  setUrlFields: (fields: Partial<UrlSyncedState>) => void
  resetUrlFilters: () => void
  resetSelectedMetric: () => void
  setSortDescriptor: (sortDescriptor: SortDescriptor) => void
}

function buildUrlStateFromConfig(config: SavedViewConfig): UrlSyncedState {
  if (!config.filters) {
    return URL_DEFAULTS
  }

  const agentIds =
    // Intentionally fall back from config.filters.agentIds to the legacy
    // config.filters.agentId shape so older saved views still hydrate correctly.
    config.filters.agentIds ||
    ((config.filters as Record<string, unknown>).agentId
      ? [(config.filters as Record<string, unknown>).agentId as string]
      : [])

  return {
    metricIds: config.filters.metricIds || [],
    agentIds,
    tagFilters: config.filters.tagFilters || [],
    projectIds: config.filters.projectIds || [],
    metadataFilters: (config.filters.metadataFilters || []).map((filter) => ({
      ...filter,
      matchType: filter.matchType ?? "EQUALS",
    })),
    metricValueFilters: (config.filters.metricValueFilters || []).map(
      (filter) => ({
        metricId: filter.metricId,
        metricDisplayName: filter.metricDisplayName,
        operator: filter.operator,
        value: filter.value,
        valueType: filter.valueType,
      }),
    ),
    dateRange:
      config.filters.startDate || config.filters.endDate
        ? {
            start: config.filters.startDate
              ? new Date(config.filters.startDate)
              : null,
            end: config.filters.endDate
              ? new Date(config.filters.endDate)
              : null,
          }
        : { start: null, end: null },
  }
}

export function useConversationsViewState({
  isReady,
  isSharedLink,
  filterState,
  setUrlFields,
  resetUrlFilters,
  resetSelectedMetric,
  setSortDescriptor,
}: UseConversationsViewStateProps) {
  const [loadedViewId, setLoadedViewId] = useState<string | null>(null)
  const columnCallbacksRef = useRef<ColumnCallbacks | null>(null)
  const viewInitializedRef = useRef(false)
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  // Scoped by org + workspace so a workspace switch can't read or write
  // another workspace's saved-view state.
  const lastViewStorageKey = useScopedStorageKey("monitoring-last-view")
  const currentStateStorageKey = useScopedStorageKey("monitoring-current-state")

  const registerColumnCallbacks = useCallback((callbacks: ColumnCallbacks) => {
    columnCallbacksRef.current = callbacks
    return () => {
      columnCallbacksRef.current = null
    }
  }, [])

  const applyViewConfig = useCallback(
    (config: SavedViewConfig) => {
      setUrlFields(buildUrlStateFromConfig(config))

      const normalizedDisplay = normalizeViewDisplayConfig(config.display)

      setSortDescriptor(normalizedDisplay.sortDescriptor)

      if (normalizedDisplay.visibleColumns && columnCallbacksRef.current) {
        columnCallbacksRef.current.onVisibleColumnsChange(
          new Set(normalizedDisplay.visibleColumns),
        )
      }
    },
    [setSortDescriptor, setUrlFields],
  )

  const loadView = useCallback(
    (view: SavedView) => {
      applyViewConfig(view.config)
      setLoadedViewId(view.ulid)
      savedViewsStorage.saveLastView(lastViewStorageKey, view.ulid)
      savedViewsStorage.saveCurrentState(currentStateStorageKey, {
        ...view.config,
        loadedViewId: view.ulid,
      })
    },
    [applyViewConfig, currentStateStorageKey, lastViewStorageKey],
  )

  const clearView = useCallback(() => {
    setLoadedViewId(null)
    savedViewsStorage.saveLastView(lastViewStorageKey, "")
    savedViewsStorage.clearCurrentState(currentStateStorageKey)

    resetUrlFilters()
    resetSelectedMetric()
    setSortDescriptor(DEFAULT_SORT_DESCRIPTOR)

    columnCallbacksRef.current?.resetToDefaults()
  }, [
    currentStateStorageKey,
    lastViewStorageKey,
    resetSelectedMetric,
    resetUrlFilters,
    setSortDescriptor,
  ])

  const detachView = useCallback(() => {
    setLoadedViewId(null)
    savedViewsStorage.saveLastView(lastViewStorageKey, "")

    const currentState = savedViewsStorage.getCurrentState(
      currentStateStorageKey,
    )
    if (currentState) {
      savedViewsStorage.saveCurrentState(currentStateStorageKey, {
        ...currentState,
        loadedViewId: null,
      })
    }
  }, [currentStateStorageKey, lastViewStorageKey])

  // Re-run when the resolved storage key changes scope (e.g. active
  // workspace changed), not just once on mount — same rationale as
  // useFilterParams's rescope handling.
  const initializedForKeyRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!isReady) {
      return
    }
    const keyChanged = initializedForKeyRef.current !== currentStateStorageKey
    if (viewInitializedRef.current && !keyChanged) {
      return
    }
    viewInitializedRef.current = true
    initializedForKeyRef.current = currentStateStorageKey

    if (isSharedLink) {
      setLoadedViewId(null)
      return
    }

    const autoSaved = savedViewsStorage.getCurrentState(currentStateStorageKey)
    if (autoSaved) {
      applyViewConfig(autoSaved)
      if (
        autoSaved.loadedViewId &&
        typeof autoSaved.loadedViewId === "string"
      ) {
        setLoadedViewId(autoSaved.loadedViewId)
        savedViewsStorage.saveLastView(
          lastViewStorageKey,
          autoSaved.loadedViewId,
        )
        return
      }
    }

    const lastViewId = savedViewsStorage.getLastView(lastViewStorageKey)
    setLoadedViewId(lastViewId)
  }, [
    applyViewConfig,
    currentStateStorageKey,
    isReady,
    isSharedLink,
    lastViewStorageKey,
  ])

  useEffect(() => {
    if (!isReady || !viewInitializedRef.current) {
      return
    }

    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current)
    }

    autoSaveTimeoutRef.current = setTimeout(() => {
      const columns = columnCallbacksRef.current
      if (!columns) {
        return
      }

      savedViewsStorage.saveCurrentState(currentStateStorageKey, {
        ...viewConfigBuilder.buildStateFromConfig(
          filterState,
          columns.getVisibleColumns(),
        ),
        loadedViewId,
      })
    }, AUTO_SAVE_DELAY)

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
      }
    }
  }, [currentStateStorageKey, filterState, isReady, loadedViewId])

  return {
    loadedViewId,
    registerColumnCallbacks,
    applyViewConfig,
    loadView,
    clearView,
    detachView,
  }
}
